/**
 * Calendar Requests Service
 * CRUD operations for calendar requests
 */

import { supabase } from "./supabase";
import type {
  UUID,
  PaginatedResponse,
  PaginationParams,
} from "../types/common.types";
import type {
  CalendarRequest,
  CalendarRequestFilters,
  CreateCalendarRequestDTO,
  UpdateCalendarRequestDTO,
} from "../types/calendarRequest.types";

/**
 * Get calendar requests with pagination and filters
 */
export async function getCalendarRequests(
  params: PaginationParams,
  filters?: CalendarRequestFilters,
): Promise<PaginatedResponse<CalendarRequest>> {
  const {
    page = 1,
    limit = 10,
    sortBy = "request_date",
    sortOrder = "desc",
  } = params;
  const start = (page - 1) * limit;
  const end = start + limit - 1;

  let query = supabase.from("calendar_requests").select(
    `
      *,
      organization:organizations(name),
      assigned_profile:profiles!calendar_requests_assigned_to_fkey(full_name),
      contact:contacts(first_name, last_name),
      location:locations(name)
    `,
    { count: "exact" },
  );

  // Apply filters
  if (filters) {
    if (filters.date_from) {
      query = query.gte("request_date", filters.date_from);
    }
    if (filters.date_to) {
      query = query.lte("request_date", filters.date_to);
    }
    if (filters.status) {
      query = query.eq("status", filters.status);
    }
    if (filters.organization_id) {
      query = query.eq("organization_id", filters.organization_id);
    }
    if (filters.assigned_to) {
      query = query.eq("assigned_to", filters.assigned_to);
    }
  }

  const { data, error, count } = await query
    .order(sortBy, { ascending: sortOrder === "asc" })
    .range(start, end);

  if (error) {
    throw error;
  }

  // Transform joined data
  const transformed = (data || []).map((item: any) => ({
    ...item,
    organization_name: item.organization?.name,
    assigned_to_name: item.assigned_profile?.full_name,
    contact_name: item.contact
      ? `${item.contact.first_name || ""} ${item.contact.last_name || ""}`.trim()
      : undefined,
    location_name: item.location?.name,
    // Remove nested objects
    organization: undefined,
    assigned_profile: undefined,
    contact: undefined,
    location: undefined,
  }));

  return {
    data: transformed as CalendarRequest[],
    total: count || 0,
    page,
    limit,
    totalPages: Math.ceil((count || 0) / limit),
  };
}

/**
 * Create a calendar request
 */
export async function createCalendarRequest(
  dto: CreateCalendarRequestDTO,
): Promise<CalendarRequest> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from("calendar_requests")
    .insert({
      ...dto,
      created_by: user?.id,
    })
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as CalendarRequest;
}

/**
 * Update a calendar request
 */
export async function updateCalendarRequest(
  id: UUID,
  dto: UpdateCalendarRequestDTO,
): Promise<CalendarRequest> {
  // Auto-set completed_at when status changes to completed
  const updateData = { ...dto };
  if (dto.status === "completed" && !dto.completed_at) {
    updateData.completed_at = new Date().toISOString();
  }

  const { data, error } = await supabase
    .from("calendar_requests")
    .update(updateData)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as CalendarRequest;
}

/**
 * Delete a calendar request
 */
export async function deleteCalendarRequest(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("calendar_requests")
    .delete()
    .eq("id", id);

  if (error) {
    throw error;
  }
}
